/**
 * "Open cPanel" for a Sonweb service on cPanel (admins only). Asks the backend for a single-use cPanel
 * session URL and opens it in a new tab, so the admin lands logged in without ever seeing a password.
 * Every click is audited and noted on the account.
 */
import React, { useState } from 'react';
import { Alert, Button, CircularProgress, IconButton, Snackbar, Tooltip } from '@mui/material';
import { OpenInNew as OpenIcon } from '@mui/icons-material';
import apiService from '../services/api';

/** The service is a cPanel web hosting service with a cPanel username set (the server decides the rest). */
export function canOpenCpanel(svc: {
  service_type?: string;
  panel_type?: string | null;
  type_specific_data?: Record<string, unknown> | null;
}): boolean {
  return (
    svc.service_type === 'sonweb' &&
    (svc.panel_type || '').trim().toLowerCase() === 'cpanel' &&
    !!String(svc.type_specific_data?.web_username ?? '').trim()
  );
}

interface Props {
  serviceId: string;
  variant?: 'button' | 'icon';
}

const OpenCpanelButton: React.FC<Props> = ({ serviceId, variant = 'button' }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = async () => {
    setError(null);
    setBusy(true);
    // Open the tab now, inside the click, so the browser doesn't block it as a popup; point it at cPanel once we have the URL
    const tab = window.open('', '_blank');
    try {
      const { url } = await apiService.adminCpanelLogin(serviceId);
      if (tab) tab.location.href = url;
      else window.open(url, '_blank');
    } catch (e: any) {
      tab?.close();
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not open cPanel.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {variant === 'icon' ? (
        <Tooltip title="Open cPanel">
          <span>
            <IconButton size="small" onClick={open} disabled={busy} aria-label="Open cPanel">
              {busy ? <CircularProgress size={16} /> : <OpenIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button variant="outlined" size="small" startIcon={busy ? <CircularProgress size={14} /> : <OpenIcon />} onClick={open} disabled={busy}>
          Open cPanel
        </Button>
      )}
      <Snackbar open={!!error} autoHideDuration={8000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
    </>
  );
};

export default OpenCpanelButton;
